import { SPACE, THEME } from '../../../../web/components-v2/Theme/theme';

/** Marks the lifecycle stage the panel is waiting on, so a live run is distinguishable at a glance. */
const stagePulse = {
	'50%': {
		opacity: 0.4,
	},
};

export const githubPanelStyles = {
	githubPanel: {
		marginTop: 18,
		padding: 14,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.light,
		flexShrink: 0,
	},
	githubPanelHeader: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 11,
	},
	githubPanelIcon: {
		flex: 'none',
		marginTop: 1,
		display: 'flex',
		// The Octocat mark reads as near-black against white, matching the prototype.
		color: THEME.colors.text.dark,
	},
	githubPanelHeading: {
		flex: 1,
		minWidth: 0,
	},
	githubPanelTitle: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	githubPanelStatus: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		flex: 'none',
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		borderRadius: 18,
		padding: '3px 9px',
	},
	githubPanelStatusDot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		backgroundColor: 'currentColor',
	},
	githubStatusSyncing: {
		color: THEME.colors.text.light,
		backgroundColor: THEME.colors.background.main,
	},
	githubStatusError: {
		color: THEME.colors.danger.dark,
		backgroundColor: THEME.colors.danger.light,
	},
	githubPanelActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		flexWrap: 'wrap' as const,
		marginTop: 13,
	},
	githubPanelPitch: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 6,
		padding: 11,
		marginTop: 12,
		border: `1px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.main,
	},
	githubPanelPitchTitle: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	githubPanelNotice: {
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.5,
		color: THEME.colors.text.light,
		marginTop: THEME.spacing.sm,
	},
	githubPanelSections: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 14,
		marginTop: 12,
	},
	githubSectionHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		marginBottom: THEME.spacing.sm,
	},
	githubSectionLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		letterSpacing: '0.06em',
		textTransform: 'uppercase' as const,
		color: THEME.colors.text.light,
	},
	githubSectionRule: {
		flex: 1,
		height: 1,
		backgroundColor: THEME.colors.border.light,
	},
	githubSectionBody: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 6,
	},
	githubRecordCard: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 9,
		padding: 11,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.light,
	},
	// The accent edge is what separates a managed record from a manually linked one at a glance.
	githubRecordCardInvestigation: {
		borderLeft: `3px solid ${THEME.colors.primary.main}`,
	},
	githubRecordCardEngineering: {
		borderLeft: `3px solid ${THEME.colors.secondary.main}`,
	},
	githubRecordTitleRow: {
		display: 'flex',
		alignItems: 'baseline',
		gap: 6,
	},
	githubRecordTitle: {
		flex: 1,
		minWidth: 0,
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		lineHeight: 1.45,
		color: THEME.colors.text.main,
		textDecoration: 'none',
		':hover': {
			color: THEME.colors.primary.main,
			textDecoration: 'underline',
		},
	},
	githubRecordRef: {
		flex: 'none',
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		textDecoration: 'none',
		':hover': { color: THEME.colors.primary.main },
	},
	githubStatusPill: {
		display: 'inline-flex',
		alignItems: 'center',
		flex: 'none',
		height: 20,
		// Off the spacing scale so the pill sits inside an 11px text row without growing it.
		padding: '0 8px',
		borderRadius: 18,
		border: '1px solid transparent',
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		whiteSpace: 'nowrap' as const,
	},
	githubPillNeutral: {
		backgroundColor: THEME.colors.background.dark,
		borderColor: THEME.colors.border.main,
		color: THEME.colors.text.main,
	},
	githubPillProgress: {
		backgroundColor: THEME.colors.primary.light,
		borderColor: THEME.colors.primary.main,
		color: THEME.colors.primary.dark,
	},
	githubPillPositive: {
		backgroundColor: THEME.colors.success.light,
		borderColor: THEME.colors.success.main,
		color: THEME.colors.text.dark,
	},
	githubPillCaution: {
		backgroundColor: THEME.colors.warning.light,
		borderColor: THEME.colors.warning.main,
		color: THEME.colors.text.dark,
	},
	githubPillCritical: {
		backgroundColor: THEME.colors.danger.light,
		borderColor: THEME.colors.danger.main,
		color: THEME.colors.text.dark,
	},
	githubPillEngineering: {
		backgroundColor: THEME.colors.secondary.light,
		borderColor: THEME.colors.secondary.main,
		color: THEME.colors.secondary.dark,
	},
	githubLifecycle: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 5,
	},
	githubLifecycleTrack: {
		display: 'flex',
		gap: 3,
	},
	githubLifecycleSegment: {
		flex: 1,
		height: 4,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.border.light,
	},
	githubLifecycleSegmentReached: {
		backgroundColor: THEME.colors.primary.main,
	},
	githubLifecycleSegmentInFlight: {
		animationName: stagePulse,
		animationDuration: '1.4s',
		animationTimingFunction: 'ease-in-out',
		animationIterationCount: 'infinite',
	},
	githubLifecycleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		flexWrap: 'wrap' as const,
	},
	githubStageText: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	githubResult: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.sm,
	},
	githubMeter: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.xs,
	},
	githubMeterRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	githubMeterDisposition: {
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	githubMeterTrack: {
		height: 5,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.border.light,
		overflow: 'hidden',
	},
	githubMeterFill: {
		height: '100%',
		borderRadius: THEME.common.borderRadius,
	},
	githubMeterFillPositive: {
		backgroundColor: THEME.colors.success.main,
	},
	githubMeterFillCaution: {
		backgroundColor: THEME.colors.warning.main,
	},
	githubMeterFillCritical: {
		backgroundColor: THEME.colors.danger.main,
	},
	githubResultSummary: {
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.55,
		color: THEME.colors.text.main,
	},
	githubRunsToggle: {
		alignSelf: 'flex-start',
		height: 'auto',
		padding: 0,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.standard,
	},
	githubRunsPanel: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 6,
		padding: '9px 10px',
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
	},
	githubRunRow: {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	githubRunRowLatest: {
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	githubRunMeta: {
		fontSize: 10,
		color: THEME.colors.text.light,
	},
	githubCardFooter: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap' as const,
		paddingTop: THEME.spacing.sm,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	githubCardFooterNote: {
		marginLeft: 'auto',
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	githubDraftStrip: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: '9px 10px',
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
	},
	githubDraftStripIcon: {
		flex: 'none',
		display: 'flex',
		color: THEME.colors.secondary.main,
	},
	githubDraftStripText: {
		flex: 1,
		minWidth: 0,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
	},
	githubPanelLoading: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	githubPanelEmpty: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	githubPanelError: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.danger.main,
		marginTop: 8,
	},
	githubPanelSectionTitle: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		marginBottom: 6,
	},
	githubPanelMeta: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	githubIssueList: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.sm,
		marginTop: THEME.spacing.sm,
	},
	githubIssueCard: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.sm,
		padding: THEME.spacing.sm,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.main,
	},
	githubIssueCardRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		flexWrap: 'wrap' as const,
	},
	githubIssueCardMain: {
		minWidth: 0,
		flex: 1,
	},
	githubIssueTitleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		flexWrap: 'wrap' as const,
		marginBottom: 4,
	},
	githubIssueTitle: {
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.primary.main,
		textDecoration: 'none',
		':hover': { textDecoration: 'underline' },
	},
	githubIssueState: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		// 1px/6px are deliberately off the spacing scale: the pill has to sit inside a 13px text row.
		padding: '1px 6px',
		borderRadius: 10,
	},
	githubIssueStateOpen: {
		backgroundColor: THEME.colors.success.light,
		color: THEME.colors.success.dark,
	},
	githubIssueStateClosed: {
		backgroundColor: THEME.colors.background.light,
		color: THEME.colors.text.light,
		border: `1px solid ${THEME.colors.border.main}`,
	},
	githubIssuePriority: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		padding: '1px 6px',
		borderRadius: 10,
		backgroundColor: THEME.colors.primary.light,
		color: THEME.colors.primary.dark,
	},
	githubIssueSummary: {
		fontSize: 13,
		color: THEME.colors.text.main,
		marginBottom: 6,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
	},
	githubIssueLabels: {
		display: 'flex',
		gap: 4,
		flexWrap: 'wrap' as const,
		marginBottom: 6,
	},
	githubIssueLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		padding: '1px 6px',
		borderRadius: 10,
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.main}`,
		color: THEME.colors.text.light,
	},
	githubStatusPillWarning: {
		backgroundColor: THEME.colors.warning.light,
		color: THEME.colors.warning.dark,
	},
	githubIssueTargetLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		padding: '1px 6px',
		borderRadius: 10,
		backgroundColor: THEME.colors.secondary.light,
		color: THEME.colors.secondary.dark,
	},
	githubIssueActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		flexWrap: 'wrap' as const,
		justifyContent: 'flex-end',
	},
	githubTriageFacts: {
		display: 'flex',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap' as const,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		marginBottom: THEME.spacing.xs,
	},
	githubTriageSummary: {
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.4,
		color: THEME.colors.text.main,
		marginBottom: THEME.spacing.xs,
	},
	githubLegacyList: {
		marginTop: 10,
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		flexWrap: 'wrap' as const,
	},
	githubLegacyLink: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.primary.main,
		textDecoration: 'none',
		padding: '2px 8px',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: 12,
		':hover': { backgroundColor: THEME.colors.primary.light },
	},
	ghTextarea: {
		width: '100%',
		minHeight: 80,
		padding: '8px 10px',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		fontSize: 13,
		fontFamily: 'inherit',
		resize: 'vertical' as const,
		boxSizing: 'border-box' as const,
		':focus': {
			outline: 'none',
			borderColor: THEME.colors.primary.main,
		},
	},
	ghIssueDialog: {
		maxWidth: 640,
		maxHeight: `calc(100vh - ${SPACE[48]}px)`,
		transition: 'max-width 0.2s ease',
	},
	ghIssueDialogWide: {
		maxWidth: 920,
	},
	ghIssueTitleInput: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	linkTicketResults: {
		maxHeight: 250,
		overflowY: 'auto' as const,
		marginTop: 8,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
	},
	linkTicketResult: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		width: '100%',
		justifyContent: 'flex-start',
		padding: '8px 12px',
		border: 'none',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 0,
		backgroundColor: 'transparent',
		cursor: 'pointer',
		fontSize: 13,
		fontWeight: 400,
		color: THEME.colors.text.main,
		textAlign: 'left' as const,
		':hover': { backgroundColor: THEME.colors.background.main },
		':last-child': { borderBottom: 'none' },
	},
	linkTicketResultNumber: {
		fontWeight: 600,
		color: THEME.colors.primary.main,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
	},
	linkTicketResultContact: {
		marginLeft: 'auto',
		fontSize: 12,
		color: THEME.colors.text.light,
		flexShrink: 0,
		maxWidth: '40%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
		textAlign: 'right' as const,
	},
	changeCustomerResultLeft: {
		display: 'flex',
		flexDirection: 'column' as const,
		minWidth: 0,
		flex: 1,
	},
	changeCustomerResultIdentity: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		minWidth: 0,
	},
	changeCustomerResultIdentityText: {
		flex: 1,
		minWidth: 0,
	},
	changeCustomerResultEmail: {
		fontSize: 12,
		color: THEME.colors.text.light,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
	},
	changeCustomerManualLink: {
		alignSelf: 'flex-start',
		padding: 0,
		height: 'auto',
		fontSize: 12,
	},
} as const;
